import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as UserActions from '../actions/user.actions';
import * as NotificationActions from '../../notification/actions/notification.actions';
import { Notification } from '../../notification/actions/notification.actions';
import { LoggerService } from '../../../service/logger.service';
import { UserService } from '../../../service/user.service';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from '../selectors/user.selectors';

@Injectable()
export class UserEffects {
  private actions$ = inject(Actions);
  private userService = inject(UserService);
  private store = inject(Store);
  private log = inject(LoggerService);

  /**
   * Effect to handle profile updates
   * Merges partial profile updates with current user and sends complete object to API
   */
  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateProfile),
      tap((action) => this.log.debug(`Updating profile: ${JSON.stringify(action.profile)}`)),
      withLatestFrom(this.store.select(selectCurrentUser)),
      switchMap(([action, currentUser]) => {
        // Merge partial profile update with current user to create complete user object
        const completeUser = {
          ...currentUser,
          ...action.profile,
        };

        this.log.debug(`Sending complete user: ${JSON.stringify(completeUser)}`);

        return this.userService.updateUser(completeUser).pipe(
          map((user) => UserActions.updateProfileSuccess({ user })),
          catchError((error) => of(UserActions.updateProfileFailure({ error: error.message }))),
        );
      }),
    ),
  );

  /**
   * Effect to show success notification for profile update
   */
  updateProfileSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateProfileSuccess),
      tap((action) =>
        this.log.debug(`Profile updated successfully: ${JSON.stringify(action.user)}`),
      ),
      map(() => {
        const notification: Notification = {
          id: Date.now().toString(),
          severity: 'success',
          title: 'Profile Updated',
          summary: 'Your profile has been updated successfully.',
          timestamp: new Date(),
          duration: 3000,
        };
        return NotificationActions.ShowNotification({ notification });
      }),
    ),
  );

  /**
   * Effect to handle avatar updates
   */
  updateAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateAvatar),
      tap((action) => this.log.debug(`Updating avatar: ${action.file.name}`)),
      switchMap((action) => {
        return this.userService.uploadAvatar(action.file).pipe(
          map((response) =>
            UserActions.updateAvatarSuccess({
              pictureUrl: response.pictureUrl,
            }),
          ),
          catchError((error) => of(UserActions.updateAvatarFailure({ error: error.message }))),
        );
      }),
    ),
  );

  /**
   * Effect to show success notification for avatar update
   */
  updateAvatarSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateAvatarSuccess),
      tap((action) => this.log.debug(`Avatar updated successfully: ${action.pictureUrl}`)),
      map(() => {
        const notification: Notification = {
          id: Date.now().toString(),
          severity: 'success',
          title: 'Avatar Updated',
          summary: 'Your profile picture has been changed successfully.',
          timestamp: new Date(),
          duration: 3000,
        };
        return NotificationActions.ShowNotification({ notification });
      }),
    ),
  );

  /**
   * Effect to load user profile
   */
  loadProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadProfile),
      tap(() => this.log.debug('Loading user profile')),
      switchMap(() => {
        // TODO: Replace with actual API call
        // return this.userService.getProfile().pipe(
        //   map((user) => UserActions.loadProfileSuccess({ user })),
        //   catchError((error) =>
        //     of(UserActions.loadProfileFailure({ error: error.message }))
        //   )
        // );

        // Temporary: Return empty success
        return of(UserActions.loadProfileSuccess({ user: {} }));
      }),
    ),
  );
}
